import { PermissionEnum } from '@Application/Security/Roles';
import type { NavigationSection } from '@UI/Routing/navigationSection';

export type AdministrationPath =
	| '/administration/users'
	| '/administration/organizations'
	| '/administration/document-groups'
	| '/administration/subject-types'
	| '/administration/roles'
	| '/administration/sso'
	| '/administration/retention'
	| '/administration/ai-engines'
	| '/administration/account-ai-assignments'
	| '/administration/organization-ai-assignments'
	| '/administration/document-templates'
	| '/administration/ai-costs'
	| '/administration/ai-cost-limits'
	| '/administration/audit-trail';

/**
 * The permission that OPENS the section, whether it grants write or only read access — the audit
 * trail is the first read-only administration screen, gated by `AUDIT_READ` alone, and no screen
 * that follows should read this field as implying the section acts.
 */
export type AdministrationSection = NavigationSection<AdministrationPath>;

/**
 * Ordered catalogue of the delivered administration screens. Order is the tab order, and also the
 * resolution order of the entry point: the first accessible section is the leftmost visible tab.
 * Every administration gate derives from this list — entry-point union, tabs, route guards and the
 * landing redirect — so a section cannot be reachable from one of them and not another.
 */
const ADMINISTRATION_SECTIONS: readonly AdministrationSection[] = [
	{
		path: '/administration/users',
		label: 'Utilisateurs',
		permission: PermissionEnum.USER_WRITE,
		action: 'gérer les utilisateurs',
	},
	{
		path: '/administration/organizations',
		label: 'Structures',
		permission: PermissionEnum.ORGANIZATION_WRITE,
		action: 'gérer les structures',
	},
	{
		path: '/administration/document-groups',
		label: 'Groupes de documents',
		permission: PermissionEnum.ORGANIZATION_WRITE,
		action: 'gérer les groupes de documents',
	},
	{
		path: '/administration/subject-types',
		label: 'Types de dossier',
		permission: PermissionEnum.SUBJECT_TYPE_MANAGE,
		action: 'gérer les types de dossier',
	},
	{
		path: '/administration/roles',
		label: 'Rôles',
		permission: PermissionEnum.ROLE_MANAGEMENT,
		action: 'gérer les rôles',
	},
	{
		path: '/administration/sso',
		label: "Fournisseur d'identité",
		permission: PermissionEnum.SSO_MANAGEMENT,
		action: "gérer le fournisseur d'identité",
	},
	{
		path: '/administration/retention',
		label: 'Rétention & purge',
		permission: PermissionEnum.ACCOUNT_RETENTION_WRITE,
		action: 'gérer les durées de conservation',
	},
	{
		path: '/administration/ai-engines',
		label: "Moteurs d'IA",
		permission: PermissionEnum.AI_MODEL_READ,
		action: "consulter les moteurs d'IA",
	},
	{
		path: '/administration/account-ai-assignments',
		label: 'Modèles du compte',
		permission: PermissionEnum.AI_ACCOUNT_ASSIGNMENT_READ,
		action: 'consulter les modèles du compte',
	},
	{
		path: '/administration/organization-ai-assignments',
		label: 'Modèles de la structure',
		permission: PermissionEnum.AI_ASSIGNMENT_READ,
		action: 'consulter les modèles de la structure',
	},
	{
		path: '/administration/document-templates',
		label: 'Gabarits de documents',
		permission: PermissionEnum.DOCUMENT_TEMPLATE_READ,
		action: 'consulter les gabarits de documents',
	},
	{
		path: '/administration/ai-costs',
		label: "Coûts d'IA",
		permission: PermissionEnum.AI_USAGE_READ,
		action: "consulter les coûts d'IA",
	},
	{
		path: '/administration/ai-cost-limits',
		label: "Plafonds de coûts d'IA",
		permission: PermissionEnum.AI_COST_LIMIT_WRITE,
		action: "régler les plafonds de coûts d'IA",
	},
	{
		path: '/administration/audit-trail',
		label: "Journal d'audit",
		permission: PermissionEnum.AUDIT_READ,
		action: "consulter le journal d'audit",
	},
];

export default ADMINISTRATION_SECTIONS;
